import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'Data/Redux';
import { wrapHigherOrderComponent } from 'Lib/React';
import React from 'react';
import { Selector, useSelector } from 'react-redux';
import deepEquals from '../../../Lib/Object/deepEquals';
import InjectedPropsContext from './InjectedPropsContext';

const injectProps = (children: any, getProviderProps: any) => {
  const child = React.Children.only(children);
  let { selectors, ...providerProps } = (getProviderProps && getProviderProps(child.props)) || {};

  if (selectors && Object.keys(selectors).length > 0) {
    return <ComponentWithInjectedPropsSelectors selectors={selectors} child={child} providerProps={providerProps} />;
  } else if (providerProps) {
    return React.cloneElement(child, { ...child.props, ...providerProps });
  } else {
    // If there are no provider props to inject, return the original child for better performance.
    return child;
  }
};

const injectedPropsSelector = (selectors: any): any =>
  createSelector([...Object.values(selectors)] as any[], (...args) =>
    args.length > 0 ? args.reduce((a: any, b: any) => ({ ...a, ...b })) : undefined
  );

/**
 *  This HOC automatically uses the given selectors and injects the props they
 *  return into the child component.
 *  Need to keep an eye on the performance of this.
 *  If processing selectors here produces too much lag,
 *  we may need to have components recognise and use individual selectors
 *  (i.e. a Field component would need to take a property called valueSelector and
 *    transform its result to value using useSelector itself)
 *  */
const ComponentWithInjectedPropsSelectors = ({ selectors, child, providerProps }: any) => {
  const selectorProps: any = useSelector(
    injectedPropsSelector(selectors),
    // Use a deep comparison to avoid triggering render unless something in the object structure changes.
    // Again, if this hurts performance we need to revise it.
    deepEquals
  );

  return React.cloneElement(child, { ...child.props, ...providerProps, ...selectorProps });
};

/**
 * This class is consumed by all core components and allows the application of props injected higher up.
 *
 * This is not the only consumer we may want, so it's not exported. It directly applies the props to its child,
 * which we do not always want to do.
 */
const InjectedPropsConsumer = ({ children }: any) => (
  <InjectedPropsContext.Consumer>
    {(getProviderProps) => injectProps(children, getProviderProps)}
  </InjectedPropsContext.Consumer>
);

/**
 * The `IWorksheetComponent` is both worksheet and non-worksheet injected props.
 * Using this here is not ideal. We shouldn't be depending on worksheets components.
 * But it's hard to avoid because the Field component needs to know about all potential props for an input.
 */
export interface IWithInjectedProps {
  alwaysEnabled?: boolean;
  /** Additional names to copy the value to when it is set by the component. */
  copyTo?: string[];
  disabled?: boolean;
  hasInjectedProps?: boolean;
  name?: string;
  /**Original label for the component. This is temporary solution for now. */
  consumerLabel?: string;
  measuredUsing?: any; // TODO: Replace with equipment data model type when we define these
  /**
   * Our injected props consumer unwraps these selectors and merges the objects they return
   * into the properties of the component
   *
   * Try to avoid using this where possible - I'm not 100% sold on this approach yet
   * */
  selectors?: {
    [id: string]: Selector<RootState, { [propName: string]: any }>;
  };
}

/**
 * Wraps a component and makes it an injected props consumer.
 *
 *
 * @param Component The component to wrap.
 * @template P Props type of the original component.
 * Note the second generic type argument is just a compilation hint (as it can be mistaken for an element in a tsx file).
 */
const withInjectedProps = <P,>(Component: React.ComponentType<P>): React.FC<P & IWithInjectedProps> =>
  wrapHigherOrderComponent(
    (props: P & IWithInjectedProps) => {
      // Implies a consumer higher up has already passed props in, e.g. a Field passing in props to a NumericInput.
      if (props.hasInjectedProps) return <Component {...props} />;

      return (
        <InjectedPropsConsumer>
          <Component {...props} />
        </InjectedPropsConsumer>
      );
    },
    Component,
    'WithInjectedProps'
  );

export { withInjectedProps };
export default withInjectedProps;
